/**
 * Logistics API module - endpoints for the carrier view.
 * Carriers (logistics companies, and suppliers a customer marked as its
 * Logistics Provider) see confirmed/dispatched/delivered call-offs, read-only.
 */
import api, { type PaginatedResponse } from './client';
import type { TruckType } from './types/call_off';

export interface LogisticsCallOff {
  id: number;
  reference: string;
  detail_status: string;
  customer: number;
  customer_name: string;
  supplier: number;
  supplier_name: string;
  pickup_date: string | null;
  dispatched_at: string | null;
  total_items: number;
  total_quantity: number;
  total_requested_qty: number;
  fill_percent: number | null;
  notes: string;
  destination_type: string;
  destination_name: string;
  total_lines: number;
  created_at: string;
  /** CR-7781: when the haulier confirmed the load (Confirm Load), or null. */
  logistics_confirmed_at: string | null;
  /** CR-9072: the truck the haulier is sending; '' until it says. */
  truck_type: TruckType | '';
}

export interface LogisticsCallOffLine {
  id: number;
  part_number: string;
  description: string;
  requested_qty: number;
  delivery_type: string;
  delivery_address: string;
  delivery_destination_name: string;
  /** CR-7744: the customer flagged this line as a priority. */
  is_priority?: boolean;
}

export interface LogisticsCallOffDetail extends Omit<LogisticsCallOff, 'total_lines'> {
  supplier_phone: string;
  supplier_address: string;
  lines: LogisticsCallOffLine[];
}

export interface LogisticsLinkedCompany {
  id: number;
  name: string;
  primary_email: string;
  primary_phone: string;
  address_line_1: string;
  postal_code: string;
  call_off_count: number;
}

export const logisticsAPI = {
  /** List confirmed/dispatched/delivered call-offs visible to the carrier */
  getCallOffs: (params?: Record<string, string | number | boolean>) =>
    api.get<PaginatedResponse<LogisticsCallOff>>('/call-off/logistics/call-offs/', { params }),

  /** Get call-off detail with lines */
  getCallOffDetail: (id: number) =>
    api.get<LogisticsCallOffDetail>(`/call-off/logistics/call-offs/${id}/`),

  /** List linked customers/companies */
  getLinkedCompanies: (params?: Record<string, string | number | boolean>) =>
    api.get<PaginatedResponse<LogisticsLinkedCompany>>('/call-off/logistics/companies/', { params }),
};
